import React, { Component } from 'react';
import { createPortal } from 'react-dom';

import { element, object } from 'prop-types';

import { MenuAPI, MenuIconType, MenuOptionType } from '@philips-emr/tws-components-api';
import { Direction } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType, ContainerProvider } from '@philips-emr/tws-core';
import { arrayOf, bool, eventListener, oneOf, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { classNames, enumValues, getSlot, isEnumValid, isFunction, isMobile, isObject, propagateProps } from '@philips-emr/tws-utils';

import { MenuGroup, MenuIcon, MenuOption } from '..';

import './menu.scss';

const bodyAcceptedTypes = [
  ComponentType.NAVIGATION,
  ComponentType.BEHAVIOR,
];

const footerAcceptedTypes = [
  ComponentType.NAVIGATION,
  ComponentType.BEHAVIOR,
];

const { HORIZONTAL, VERTICAL } = Direction;

@AwesomeComponent({ type: ComponentType.NAVIGATION, api: MenuAPI })
class Menu extends Component {
  state = { };

  static propTypes = {
    baseName: string.readOnly(),
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    className: string,
    data: arrayOf(object).readOnly(),
    direction: oneOf(enumValues(Direction)),
    dockedSubmenu: bool,
    showHeader: bool,
    submenu: bool.readOnly(),
    onBeforeClose: eventListener,
    onClose: eventListener,
    onOpen: eventListener,
    iconTarget: oneOfType([element, object]).notEnumerable(),
    uuid: string,
  };

  static defaultProps = {
    direction: VERTICAL,
    dockedSubmenu: true,
    showHeader: false,
    submenu: false,
  };

  closeMenu(event) {
    const { onClose } = this.props;

    isFunction(onClose) && onClose(event);
  }

  openMenu() {
    const { onOpen } = this.props;

    isFunction(onOpen) && onOpen();
  }

  onOptionClick = (event) => {
    if (event.value) {
      this.closeMenu(event);
    }
  };

  onOptionClose = (event) => {

    if (!this.shouldClose()) {
      return;
    }

    if (isMobile()) {
      this.setState({ hasOpenOption: false });
    }

    if (event.value) {
      this.closeMenu(event);
    }
  };

  onOptionOpen = () => {
    if (isMobile()) {
      this.setState({ hasOpenOption: true });
    }

    this.openMenu();
  };

  shouldClose = () => {
    const { onBeforeClose } = this.props;

    let close = true;
    const abort = () => close = false;
    
    isFunction(onBeforeClose) && onBeforeClose({ abort });

    return close;
  }

  getClassName() {
    const { className, submenu, direction, dockedSubmenu } = this.props;

    return classNames({
      'c-menu': true,
      'c-menu--submenu': submenu,
      'c-menu--docked': submenu && dockedSubmenu,
      [`c-menu--${direction}`]: isEnumValid(Direction, direction),
      [className]: Boolean(className),
    });
  }

  menuRef = (menuElement) => {
    menuElement && this.setState({ menuElement });
  };

  getOptionProps() {
    const { baseName, direction, dockedSubmenu, iconTarget } = this.props;
    const { menuElement } = this.state;

    return {
      baseName,
      direction: direction === VERTICAL ? HORIZONTAL : VERTICAL,
      dockedSubmenu,
      iconTarget,
      onClick: this.onOptionClick,
      onClose: this.onOptionClose,
      onOpen: this.onOptionOpen,
      root: menuElement,
    };
  }

  renderIcon() {
    const { iconTarget, onClose, submenu } = this.props;
    const { hasOpenOption } = this.state;

    if (hasOpenOption || !iconTarget || (submenu && !isMobile())) {
      return null;
    }

    const menuIconType = submenu ? MenuIconType.BACK : MenuIconType.CLOSE;

    const content = (
      <MenuIcon onClick={onClose} type={menuIconType} />
    );

    return createPortal(content, iconTarget.current);
  }

  renderSlot(slot) {
    const children = getSlot(this.props.children, slot);
    const { menuElement } = this.state;

    if (!menuElement) {
      return null;
    }

    const optProps = this.getOptionProps();
    return propagateProps(children, optProps);
  }

  renderFromData() {
    const { data } = this.props;
    const { menuElement } = this.state;

    if (!menuElement || !data) {
      return null;
    }

    if (isObject(data)) {
      return this.renderGroups(data);
    }

    if (Array.isArray(data)) {
      return this.renderOptions(data);
    }

    return null;
  }

  renderGroups(data) {
    const dataEntries = Object.entries(data);

    return dataEntries.map(([title, options], index) => (
      <MenuGroup key={index} id={index} title={title} >
        {this.renderOptions(options)}
      </MenuGroup>
    ));
  }

  renderOptions(data) {
    return (
      data.map((item, index) => {
        const optProps = this.getOptionProps();

        return (
          <MenuOption key={item.key || index} {...optProps} {...item} >
            {item.children ? <Menu data={item.children}/> : null}
          </MenuOption>
        );
      })
    );
  }

  render() {
    const { onClose, submenu, uuid } = this.props;
    const Container = ContainerProvider.provide(this);

    return (
      <div className={this.getClassName()} ref={this.menuRef} x-contrast="bg bd">
        {onClose && this.renderIcon()}

        <Container className="c-menu__body" acceptedTypes={bodyAcceptedTypes} slot="body" uuid={uuid} x-contrast="bg">
          {this.renderSlot('body')}
          {this.renderFromData()}
        </Container>

        {!submenu &&
        <Container className="c-menu__footer" acceptedTypes={footerAcceptedTypes} slot="footer" uuid={uuid} payload={{ type: MenuOptionType.SECONDARY }} x-contrast="bg" >
          {this.renderSlot('footer')}
        </Container>}
      </div>
    );
  }
}

export default Menu;
